import { apiAiFrameworkApprovals } from '@modules/ai-framework/api/apiAiFrameworkApprovals'
import { APPROVALS } from '@modules/ai-framework/constants/copy'

// Slice `approvals` (spec-004 G14) — sub-módulo namespaced sob `aiFramework`
// (`aiFramework/approvals/*`). Toda action lê `r.status` do BODY (HTTP-200-always).
// ★ A UI nunca finge execução: o snack de desfecho é dirigido por `r.value.commit_status`
// (executado/escopo_invalido/gate_revogado/erro) — "criada" só quando `executado`.
const STATUS_OK = 200
const STATUS_BAD_REQUEST = 400
const STATUS_FORBIDDEN = 403
const STATUS_CONFLICT = 409

// Desfecho honesto por commit_status. Nenhum caminho promete "criada" quando não foi.
const COMMIT_STATUS_SNACK = {
  executado: { text: APPROVALS.SNACK_EXECUTADO, color: 'success' },
  escopo_invalido: { text: APPROVALS.SNACK_ESCOPO_INVALIDO, color: 'warning' },
  gate_revogado: { text: APPROVALS.SNACK_GATE_REVOGADO, color: 'warning' },
  erro: { text: APPROVALS.SNACK_ERRO_COMMIT, color: 'warning' },
}

function decisionKey(idRun, ordemStep) {
  return String(idRun) + ':' + String(ordemStep)
}

function commitStatusSnack(commitStatus) {
  const snack = COMMIT_STATUS_SNACK[commitStatus] || COMMIT_STATUS_SNACK.erro
  return { text: snack.text, color: snack.color, button: true }
}

const state = () => ({
  pending: [], // PendingProposalItem[]
  total: 0,
  loadingPending: false,
  deciding: {}, // { `${idRun}:${ordemStep}`: true } — trava o item durante a decisão
  forbidden: false, // 403 (flag-mestra OFF ou não-gestor)
  filters: { idInstance: null, idProfissional: null, de: null, ate: null },
})

const getters = {
  pendingApprovals: (s) => s.pending || [],
  pendingCount: (s) => s.total || 0, // ★ badge da aba (oculto quando 0 — NC-16)
  loadingPending: (s) => s.loadingPending,
  approvalsForbidden: (s) => s.forbidden,
  approvalsFilters: (s) => s.filters || {},
  isDeciding: (s) => (idRun, ordemStep) => Boolean(s.deciding[decisionKey(idRun, ordemStep)]),
}

const mutations = {
  SET_PENDING(s, payload) {
    s.pending = payload || []
  },
  SET_TOTAL(s, payload) {
    s.total = payload || 0
  },
  SET_LOADING(s, payload) {
    s.loadingPending = payload
  },
  SET_FORBIDDEN(s, payload) {
    s.forbidden = payload
  },
  SET_FILTERS(s, payload) {
    s.filters = { ...s.filters, ...payload }
  },
  SET_DECIDING(s, { idRun, ordemStep, value }) {
    const key = decisionKey(idRun, ordemStep)
    const next = { ...s.deciding }
    if (value) {
      next[key] = true
    } else {
      delete next[key]
    }
    s.deciding = next
  },
  REMOVE_ITEM(s, { idRun, ordemStep }) {
    s.pending = (s.pending || []).filter(
      (item) => !(item.id_run === idRun && item.ordem_step === ordemStep)
    )
  },
}

// Aplica o resultado da consulta da fila (extraído para manter a action enxuta).
function applyPendingResult({ commit, dispatch }, r) {
  if (r && r.status === STATUS_OK) {
    const value = r.value || {}
    commit('SET_PENDING', value.items || [])
    commit('SET_TOTAL', value.total || 0)
    commit('SET_FORBIDDEN', false)
    return
  }
  if (r && r.status === STATUS_FORBIDDEN) {
    commit('SET_PENDING', [])
    commit('SET_TOTAL', 0)
    commit('SET_FORBIDDEN', true)
    return
  }
  commit('SET_PENDING', [])
  commit('SET_TOTAL', 0)
  dispatch('feedback/showSnack', r, { root: true })
}

const actions = {
  async fetchPending({ commit, dispatch, state }, filters) {
    if (filters) commit('SET_FILTERS', filters)
    commit('SET_LOADING', true)
    const r = await apiAiFrameworkApprovals.listPending(state.filters)
    applyPendingResult({ commit, dispatch }, r)
    commit('SET_LOADING', false)
    return r
  },

  async approve({ commit, dispatch }, { idRun, ordemStep, justificativa = null } = {}) {
    commit('SET_DECIDING', { idRun, ordemStep, value: true })

    const r = await apiAiFrameworkApprovals.approve(idRun, ordemStep, { justificativa })

    if (r && r.status === STATUS_OK) {
      const commitStatus = (r.value && r.value.commit_status) || 'erro'
      dispatch('feedback/showSnack', commitStatusSnack(commitStatus), { root: true })
      commit('REMOVE_ITEM', { idRun, ordemStep })
      dispatch('fetchPending')
    } else if (r && r.status === STATUS_CONFLICT) {
      // Outro gestor decidiu primeiro: item some, re-consulta a fila (nunca dupla escrita).
      dispatch('feedback/showSnack', { text: APPROVALS.SNACK_JA_DECIDIDA, color: 'warning', button: true }, { root: true })
      dispatch('fetchPending')
    } else {
      // 500 (atomicidade), 422 (drift), 404 (opaco): a proposta PERMANECE na fila —
      // nada foi escrito. Mensagens de gate opacas usam o TEXTO EXATO de r.message.
      dispatch('feedback/showSnack', buildErrorSnack(r), { root: true })
    }

    commit('SET_DECIDING', { idRun, ordemStep, value: false })
    return r
  },

  async reject({ commit, dispatch }, { idRun, ordemStep, justificativa } = {}) {
    commit('SET_DECIDING', { idRun, ordemStep, value: true })

    const r = await apiAiFrameworkApprovals.reject(idRun, ordemStep, { justificativa })

    if (r && r.status === STATUS_OK) {
      dispatch('feedback/showSnack', { text: APPROVALS.SNACK_RECUSADO, color: 'success', button: true }, { root: true })
      commit('REMOVE_ITEM', { idRun, ordemStep })
      dispatch('fetchPending')
    } else if (r && r.status === STATUS_CONFLICT) {
      dispatch('feedback/showSnack', { text: APPROVALS.SNACK_JA_DECIDIDA, color: 'warning', button: true }, { root: true })
      dispatch('fetchPending')
    } else if (r && r.status === STATUS_BAD_REQUEST) {
      // Justificativa vazia (defesa em profundidade — o modal também barra). Erro inline:
      // a view/modal exibe r.message; sem re-consulta e sem remover o item.
      dispatch('feedback/showSnack', buildErrorSnack(r), { root: true })
    } else {
      dispatch('feedback/showSnack', buildErrorSnack(r), { root: true })
    }

    commit('SET_DECIDING', { idRun, ordemStep, value: false })
    return r
  },
}

// Snack de erro/gate: TEXTO EXATO do backend (opacidade do eixo) com fallback amigável.
function buildErrorSnack(r) {
  const text = (r && r.message) || APPROVALS.SNACK_FALHA_ATOMICIDADE
  return { text: text, color: 'error', button: true }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
